import { useRef, useEffect, forwardRef, useState, useCallback, useImperativeHandle, createElement, memo, createRef } from 'react';
import { Block, Text } from 'slate';
import { Range } from 'immutable';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation. All rights reserved.
Licensed under the Apache License, Version 2.0 (the "License"); you may not use
this file except in compliance with the License. You may obtain a copy of the
License at http://www.apache.org/licenses/LICENSE-2.0

THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
MERCHANTABLITY OR NON-INFRINGEMENT.

See the Apache Version 2.0 License for specific language governing permissions
and limitations under the License.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __spreadArrays() {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
}

var defaultOptions = {
    typeCell: 'table_cell',
    typeRow: 'table_row',
    typeTable: 'table',
    typeContent: 'table_content',
    selectionColor: '#B9D3FC',
    minimumCellWidth: 32,
    cellStyle: { padding: '3px' },
    rowStyle: {},
    tableStyle: {},
    defaultColumnWidth: 15,
};

// INFO: Deprecated.
//       Use createLayout and create new function to create cell's Dictionary
function buildTableLayout(nodes) {
    var layout = createLayout(nodes);
    var keyDict = layout.reduce(function (acc, row) {
        row.forEach(function (cell) {
            if (acc[cell.key])
                return;
            acc[cell.key] = cell;
        });
        return acc;
    }, {});
    return { layout: layout, keyDict: keyDict };
}
// Deprecated
var TableLayout = /** @class */ (function () {
    function TableLayout(TableLayout, dict, table, row, cell) {
        this.table = [];
        this.table = TableLayout;
        this.keyDict = dict;
        this.currentTable = table;
        this.row = row;
        this.cell = cell;
    }
    Object.defineProperty(TableLayout.prototype, "col", {
        get: function () {
            return this.table[0].length;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TableLayout.prototype, "firstRow", {
        get: function () {
            return this.table[0];
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TableLayout.prototype, "firstColumn", {
        get: function () {
            return this.table.map(function (row) {
                return row[0];
            });
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TableLayout.prototype, "lastRow", {
        get: function () {
            return this.table[this.table.length - 1];
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TableLayout.prototype, "lastColumn", {
        get: function () {
            return this.table.map(function (row) {
                return row[row.length - 1];
            });
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TableLayout.prototype, "rowIndex", {
        get: function () {
            var _a = this, currentTable = _a.currentTable, row = _a.row;
            var rows = currentTable.nodes;
            return rows.findIndex(function (x) { return x === row; });
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TableLayout.prototype, "width", {
        get: function () {
            var table = this.table;
            return table[0].length;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TableLayout.prototype, "height", {
        get: function () {
            var currentTable = this.currentTable;
            var rows = currentTable.nodes;
            return rows.size;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TableLayout.prototype, "columnIndex", {
        get: function () {
            var _a = this, row = _a.row, cell = _a.cell;
            var cells = row.nodes;
            var r = cells.reduce(function (acc, x) {
                if (acc === void 0) { acc = { has: false, pos: 0, prevColspan: 0 }; }
                if (!Block.isBlock(x))
                    return acc;
                var colspan = Number(x.data.get('colspan') || 1);
                var pos = acc.has ? acc.pos : acc.pos + acc.prevColspan;
                return {
                    has: acc.has || x === cell,
                    pos: pos,
                    prevColspan: colspan,
                };
            }, { has: false, pos: 0, prevColspan: 0 });
            return r.has ? r.pos : -1;
        },
        enumerable: true,
        configurable: true
    });
    TableLayout.prototype.isTopOfMergedCell = function () { };
    TableLayout.prototype.findCellBy = function (key) {
        for (var _i = 0, _a = this.table; _i < _a.length; _i++) {
            var row = _a[_i];
            for (var _b = 0, row_1 = row; _b < row_1.length; _b++) {
                var cell = row_1[_b];
                if (cell.key === key)
                    return cell;
            }
        }
    };
    TableLayout.prototype.findNextRowKey = function () {
        var next = this.table[this.rowIndex + 1];
        return next[0].rowKey;
    };
    TableLayout.prototype.filterHorizontalMergedCellsBy = function (columnIndex) {
        var cells = [];
        this.table.forEach(function (row) {
            var cell = row[columnIndex];
            if (cell.colspan > 1) {
                cells.push(cell);
            }
        });
        return cells;
    };
    TableLayout.prototype.filterVerticalMergedCellsBy = function (rowIndex) {
        return this.table[rowIndex].filter(function (cell) { return cell.rowspan > 1; });
    };
    TableLayout.prototype.getRows = function (rowIndex) {
        return this.table[rowIndex];
    };
    TableLayout.prototype.findBelow = function (key) {
        var cell = this.findCellBy(key);
        if (!cell)
            return;
        var y = 0;
        var t = this.table[cell.row][cell.col];
        while (t && t.key === key) {
            y++;
            if (!this.table[cell.row + y] || !this.table[cell.row + y][cell.col])
                return;
            t = this.table[cell.row + y][cell.col];
        }
        return t;
    };
    TableLayout.prototype.getNextRowSize = function (key) {
        var cell = this.findCellBy(key);
        if (!cell)
            return 0;
        var y = 0;
        var l = 0;
        var t = this.table[cell.row][cell.col];
        while (t && t.key === key) {
            y++;
            l = this.table[cell.row + y].length;
            t = this.table[cell.row + y][cell.col];
        }
        return l;
    };
    TableLayout.prototype.getRight = function (key) {
        var cell = this.findCellBy(key);
        if (!cell)
            return;
        var x = 0;
        var t = this.table[cell.row][cell.col + x];
        while (t && t.key === key) {
            x++;
            t = this.table[cell.row][cell.col + x];
        }
        return t;
    };
    TableLayout.prototype.isLastColumn = function (key) {
        return this.lastColumn.some(function (c) { return c.key === key; });
    };
    TableLayout.prototype.isLastRow = function (key) {
        return this.lastRow.some(function (r) { return r.key === key; });
    };
    TableLayout.create = function (editor, opts) {
        if (!editor)
            return null;
        var table = findCurrentTable(editor, opts);
        if (!table)
            return null;
        var nodes = table && table.getNode(table.key).nodes;
        if (!nodes)
            return null;
        var _a = buildTableLayout(nodes), layout = _a.layout, keyDict = _a.keyDict;
        var currentCell = TableLayout.currentCell(editor, opts);
        var currentRow = TableLayout.currentRow(editor, opts);
        var currentTable = TableLayout.currentTable(editor, opts);
        if (!currentCell)
            return null;
        return new TableLayout(layout, keyDict, currentTable, currentRow, currentCell);
    };
    TableLayout.currentCell = function (editor, opts) {
        return findStartCell(editor, opts);
    };
    TableLayout.findBlock = function (editor, type) {
        var value = editor.value;
        return value.document.getClosest(value.startBlock.key, function (p) {
            if (!Block.isBlock(p))
                return false;
            return p.type === type;
        });
    };
    TableLayout.findRowBlock = function (editor, index, opts) {
        var table = TableLayout.currentTable(editor, opts);
        if (!table)
            return;
        var block = table.nodes.get(index);
        return Block.isBlock(block) ? block : undefined;
    };
    TableLayout.currentRow = function (editor, opts) {
        return TableLayout.findBlock(editor, opts.typeRow);
    };
    TableLayout.currentTable = function (editor, opts) {
        return TableLayout.findBlock(editor, opts.typeTable);
    };
    TableLayout.isInCell = function (editor, opts) {
        if (opts === void 0) { opts = defaultOptions; }
        var value = editor.value;
        var startBlock = value.startBlock;
        return startBlock.type === opts.typeCell ||
            value.document.getClosest(startBlock.key, function (p) {
                if (!Block.isBlock(p))
                    return false;
                return p.type === opts.typeCell;
            })
            ? true
            : false;
    };
    return TableLayout;
}());
function findCurrentTable(editor, opts) {
    if (opts === void 0) { opts = defaultOptions; }
    if (!editor)
        return null;
    var value = editor.value;
    var startBlock = value.startBlock;
    if (!startBlock)
        return null;
    var table = value.document.getClosest(startBlock.key, function (p) { return p.type === opts.typeTable; });
    if (!table)
        return null;
    if (!Block.isBlock(table))
        return null;
    return table;
}
function findCurrentRow(editor, opts) {
    if (opts === void 0) { opts = defaultOptions; }
    var value = editor.value;
    return (value.document.getClosest(value.startBlock.key, function (p) {
        if (!Block.isBlock(p))
            return false;
        return p.type === opts.typeRow;
    }) || null);
}
// INFO: We can not get expected block with Value.focusBlock.
//       We need to do some investigation about it.
//       For now find focus cell with window.getSelection.
function findFocusCell(editor, opts) {
    if (opts === void 0) { opts = defaultOptions; }
    var selection = window.getSelection();
    if (!selection)
        return null;
    var focused = selection.focusNode;
    if (!focused)
        return null;
    return findCellBlockByElement(editor, focused, opts);
}
// INFO: We can not get expected block with Value.anchorBlock.
//       We need to do some investigation about it.
//       For now find anchor cell with window.getSelection.
function findAnchorCell(editor, opts) {
    var selection = window.getSelection();
    if (!selection)
        return null;
    var anchored = selection.anchorNode;
    if (!anchored)
        return null;
    return findCellBlockByElement(editor, anchored, opts);
}
function findStartCell(editor, opts) {
    var startBlock = editor.value.startBlock;
    return startBlock.type === opts.typeCell ? startBlock : TableLayout.findBlock(editor, opts.typeCell);
}
function findClosestKey(el) {
    if (el.dataset && el.dataset.key) {
        return el.dataset.key;
    }
    if (!el.parentElement) {
        return null;
    }
    return findClosestKey(el.parentElement);
}
function findCellBlockByElement(editor, el, opts) {
    if (opts === void 0) { opts = defaultOptions; }
    var value = editor.value;
    var key = findClosestKey(el);
    if (typeof key === 'undefined' || key === null) {
        return null;
    }
    var found = value.document.getClosest(key, function (p) {
        if (!Block.isBlock(p))
            return false;
        if (p.type === opts.typeRow) {
            return true;
        }
        return p.type === opts.typeCell;
    });
    if (found.type === opts.typeCell) {
        return found;
    }
    if (found.type === opts.typeRow) {
        return found.nodes.find(function (cell) {
            if (!Block.isBlock(cell))
                return false;
            return cell.key === key;
        });
    }
    return null;
}
function findLeftTopPosition(anchor, focus) {
    return [Math.min(anchor.row, focus.row), Math.min(anchor.col, focus.col)];
}
function createSelectedCellRange(anchor, focus) {
    var start = {
        row: Math.min(anchor.row, focus.row),
        col: Math.min(anchor.col, focus.col),
    };
    var end = {
        row: Math.max(anchor.row, focus.row),
        col: Math.max(anchor.col, focus.col),
    };
    return { start: start, end: end };
}
function calculateSelectedCellSize(anchor, focus) {
    var height = Math.abs(anchor.row - focus.row) + (anchor.row < focus.row ? focus.rowspan : anchor.rowspan);
    var width = Math.abs(anchor.col - focus.col) + (anchor.col < focus.col ? focus.colspan : anchor.colspan);
    return { width: width, height: height };
}
function createSelectedBlockMap(editor, anchorKey, focusKey, opts) {
    var t = TableLayout.create(editor, opts);
    if (!t)
        return {};
    var anchor = t.keyDict[anchorKey];
    var focus = t.keyDict[focusKey];
    if (!anchor || !focus)
        return {};
    var _a = createSelectedCellRange(anchor, focus), start = _a.start, end = _a.end;
    return t.table.reduce(function (acc, row, rowIndex) {
        row.forEach(function (cell, columnIndex) {
            if (rowIndex >= start.row && rowIndex <= end.row && columnIndex >= start.col && columnIndex <= end.col) {
                acc[cell.key] = cell;
            }
        });
        return acc;
    }, {});
}
function getRowIndex(editor, opts) {
    var t = findCurrentTable(editor, opts);
    var row = findCurrentRow(editor, opts);
    if (!t)
        return null;
    var rows = t.nodes;
    var i = rows.findIndex(function (x) { return x === row; });
    if (i < 0)
        return null;
    return i;
}
function collectSelectionBlocks(editor, anchorKey, focusKey, opts) {
    var t = TableLayout.create(editor, opts);
    if (!t)
        return [];
    var anchor = t.keyDict[anchorKey];
    var focus = t.keyDict[focusKey];
    var _a = createSelectedCellRange(anchor, focus), start = _a.start, end = _a.end;
    return t.table
        .map(function (row, rowIndex) {
        var newRow = row.filter(function (cell, columnIndex) {
            return rowIndex >= start.row && rowIndex <= end.row && columnIndex >= start.col && columnIndex <= end.col;
        });
        return newRow.length ? newRow : null;
    })
        .filter(notNull);
}
function createLayout(rows) {
    var rowLength = rows.size;
    var colLength = Math.max.apply(Math, Array.from(rows.toArray()).map(function (node) {
        if (!Block.isBlock(node))
            return 0;
        return node.nodes.reduce(function (acc, n) {
            if (acc === void 0) { acc = 0; }
            if (!Block.isBlock(n))
                return acc;
            return acc + ((n.data && Number(n.data.get('colspan'))) || 1);
        }, 0);
    }));
    var layout = [];
    for (var row = 0, cellY = 0; row < rowLength; row++, cellY++) {
        var nodeIndex = 0;
        var cellX = 0;
        for (var col = 0; col < colLength; col++) {
            if (layout[row] && layout[row][col]) {
                continue;
            }
            if (!layout[row]) {
                layout[row] = [];
            }
            var rowBlock = rows.get(cellY);
            if (!Block.isBlock(rowBlock))
                continue;
            var cell = rowBlock && rowBlock.nodes.get(nodeIndex);
            var rowKey = rowBlock.key;
            if (!cell) {
                throw new Error('[slate-editable-table-plugin]: Should cell exist');
            }
            var key = cell.key;
            var colspan = Number((cell && cell.data.get('colspan')) || 1);
            var rowspan = Number((cell && cell.data.get('rowspan')) || 1);
            layout[row][col] = {
                key: key,
                row: row,
                col: col,
                colspan: colspan,
                rowspan: rowspan,
                block: cell,
                rowKey: rowKey,
                rowBlock: rowBlock,
                nodeIndex: nodeIndex,
                isLeftOfMergedCell: cellX === 0,
                isTopOfMergedCell: rowspan > 1,
            };
            if (rowspan > 1) {
                for (var r = 0; r < rowspan - 1; r++) {
                    if (!layout[row + r + 1]) {
                        layout[row + r + 1] = [];
                    }
                    layout[row + r + 1][col] = {
                        key: key,
                        row: row + r + 1,
                        col: col,
                        colspan: colspan,
                        rowspan: rowspan,
                        block: cell,
                        rowKey: rowKey,
                        nodeIndex: nodeIndex,
                        rowBlock: rowBlock,
                        isLeftOfMergedCell: cellX === 0,
                        isTopOfMergedCell: false,
                    };
                }
            }
            if (cellX === colspan - 1) {
                cellX = 0;
                nodeIndex++;
            }
            else {
                cellX++;
            }
        }
    }
    return layout;
}
function notNull(item) {
    return item !== null;
}
function isInCell(editor, block, opts) {
    if (opts === void 0) { opts = defaultOptions; }
    return block.type === opts.typeCell ||
        editor.value.document.getClosest(block.key, function (p) {
            if (!Block.isBlock(p))
                return false;
            return p.type === opts.typeCell;
        })
        ? true
        : false;
}

var insertStyleId = '__slate__table__id';
function removeSelection(editor) {
    editor.withoutSaving(function () {
        var editors = document.querySelectorAll('[data-slate-editor]');
        Array.from(editors).forEach(function (e) {
            var tables = e.querySelectorAll('table');
            tables.forEach(function (table) {
                var key = table.dataset.key;
                if (!key)
                    return;
                var tableBlock = editor.value.document.getNode(key);
                if (!Block.isBlock(tableBlock))
                    return;
                tableBlock.nodes.forEach(function (row) {
                    if (!Block.isBlock(row))
                        return;
                    row.nodes.forEach(function (cell) {
                        if (!Block.isBlock(cell))
                            return;
                        editor.setNodeByKey(cell.key, {
                            type: cell.type,
                            data: __assign(__assign({}, cell.data.toObject()), { selectionColor: null }),
                        });
                    });
                });
            });
        });
        removeSelectionStyle();
    });
}
function removeSelectionStyle() {
    var style = document.querySelector("style#" + insertStyleId);
    if (style) {
        var head = document.getElementsByTagName('head');
        var first = head && head.item(0);
        first && first.removeChild(style);
    }
}
function addSelectionStyle() {
    // HACK: Add ::selection style when greater than 1 cells selected.
    if (!document.querySelector("style#" + insertStyleId)) {
        var style = document.createElement('style');
        style.type = 'text/css';
        style.id = insertStyleId;
        var head = document.getElementsByTagName('head');
        var first = head && head.item(0);
        if (first) {
            first.appendChild(style);
            var stylesheet = style.sheet;
            if (stylesheet) {
                stylesheet.insertRule("table *::selection { background: none; }", stylesheet.cssRules.length);
            }
        }
    }
}

function mergeCells(editor, anchorKey, focusKey, opts) {
    var table = TableLayout.create(editor, opts);
    if (!table)
        return;
    var anchorCell = table.findCellBy(anchorKey);
    var focusCell = table.findCellBy(focusKey);
    if (!anchorCell || !focusCell)
        return;
    var size = calculateSelectedCellSize(anchorCell, focusCell);
    var _a = findLeftTopPosition(anchorCell, focusCell), row = _a[0], col = _a[1];
    var leftTopCell = table.table[row][col];
    if (!canMerge(editor, anchorCell.key, focusCell.key, opts)) {
        return editor;
    }
    // INFO: Count deleted row number
    //       We use it to calculate rowspan
    var deletedRowNumber = 0;
    // INFO: To store content blocks in loop and insertBlock later.
    //       This is because it does not work properly when insertBlock in loop..
    //       I'll do some investigation later... :sob:
    editor.withoutNormalizing(function () {
        var blocks = createSelectedBlockMap(editor, anchorCell.key, focusCell.key, opts);
        var contentNodes = [];
        var firstRow = null;
        var newWidth = 0;
        table.table.forEach(function (row) {
            row.forEach(function (cell) {
                if (!blocks[cell.key])
                    return;
                // calculate new width
                var n = editor.value.document.getNode(cell.key);
                if (Block.isBlock(n)) {
                    if (firstRow === null) {
                        firstRow = cell.row;
                    }
                    // Calculate only first row.
                    if (firstRow === cell.row) {
                        newWidth = newWidth + ~~cell.block.data.get('width') / (cell.block.data.get('colspan') || 1);
                    }
                }
                if (cell.key === leftTopCell.key)
                    return;
                // INFO: Collect all content and remove cells
                cell.block.nodes.forEach(function (content, i) {
                    var newLeftTopCell = editor.value.document.getNode(leftTopCell.key);
                    if (!Block.isBlock(newLeftTopCell) || !Block.isBlock(content))
                        return;
                    var tempContent = editor.value.document.getNode(content.key);
                    var newContentBlock = tempContent && tempContent.regenerateKey();
                    if (!Block.isBlock(newContentBlock))
                        return;
                    contentNodes.push(newContentBlock);
                    if (i != cell.block.nodes.size - 1)
                        return;
                    editor = editor.removeNodeByKey(cell.key);
                });
                // INFO: Remove unnecessary row.
                // After removing some cells, If row is empty delete row and reduce related cell's rowspan.
                var newRow = editor.value.document.getNode(cell.rowBlock.key);
                if (!Block.isBlock(newRow))
                    return;
                if ((newRow.nodes.size === 1 && Text.isText(newRow.nodes.get(0))) || newRow.nodes.size === 0) {
                    deletedRowNumber++;
                    editor = editor.removeNodeByKey(newRow.key);
                    row.forEach(function (cell) {
                        if (cell.rowspan === 1)
                            return;
                        var newCell = editor.value.document.getNode(cell.key);
                        if (Block.isBlock(newCell)) {
                            editor.setNodeByKey(newCell.key, {
                                type: newCell.type,
                                data: __assign(__assign({}, newCell.data.toObject()), { rowspan: String(cell.rowspan - deletedRowNumber) }),
                            });
                        }
                    });
                }
            });
        });
        // INFO: Set merged cell's rowspan and colspan
        editor = editor.setNodeByKey(leftTopCell.key, {
            type: leftTopCell.block.type,
            data: __assign(__assign({}, leftTopCell.block.data.toObject()), { width: String(newWidth), colspan: String(size.width), rowspan: String(size.height - deletedRowNumber) }),
        });
        // INFO: Insert contents to merged cell.
        contentNodes.forEach(function (node) {
            var newLeftTopCell = editor.value.document.getNode(leftTopCell.key);
            if (Block.isBlock(newLeftTopCell)) {
                if (node.type !== 'table' && node.nodes.size === 1 && node.getText().trim() === '')
                    return;
                editor = editor.moveToEndOfNode(newLeftTopCell).insertBlock(node);
            }
        });
        removeSelection(editor);
    });
    return editor;
}
function isValidHeight(selectedBlocks) {
    var stored = {};
    var verticalLayout = selectedBlocks.reduce(function (acc, row, rowIndex) {
        row.forEach(function (cell, colIndex) {
            if (stored[cell.key + cell.col])
                return;
            if (rowIndex === 0) {
                acc[colIndex] = { y: cell.row, height: cell.rowspan };
            }
            else {
                acc[colIndex] = __assign(__assign({}, acc[colIndex]), { height: ((acc[colIndex] && acc[colIndex].height) || 0) + cell.rowspan });
            }
            stored[cell.key + cell.col] = true;
        });
        return acc;
    }, {});
    var isHeightValid = Object.keys(verticalLayout).reduce(function (acc, key, i) {
        if (!acc.isHeightValid)
            return { isHeightValid: false, prev: verticalLayout[key] };
        if (i === 0)
            return { isHeightValid: true, prev: verticalLayout[key] };
        if (acc.prev.y === verticalLayout[key].y && acc.prev.height === verticalLayout[key].height)
            return acc;
        return { isHeightValid: false, prev: verticalLayout[key] };
    }, { isHeightValid: true, prev: {} }).isHeightValid;
    return isHeightValid;
}
function canMerge(editor, anchorKey, focusKey, opts) {
    var selectedBlocks = collectSelectionBlocks(editor, anchorKey, focusKey, opts);
    var a = selectedBlocks.map(function (row) {
        var x = row[0].col;
        var width = row.reduce(function (acc, cell) {
            var _a;
            if (acc.stored[cell.key])
                return acc;
            return { width: acc.width + cell.colspan, stored: __assign(__assign({}, acc.stored), (_a = {}, _a[cell.key] = true, _a)) };
        }, { width: 0, stored: {} }).width;
        return { x: x, width: width };
    });
    var isWidthValid = a.every(function (c, _, arr) { return c.x === arr[0].x && c.width === arr[0].width; });
    if (!isWidthValid)
        return false;
    return isValidHeight(selectedBlocks);
}

function removeRow(opts, editor, at) {
    if (opts === void 0) { opts = defaultOptions; }
    var table = TableLayout.create(editor, opts);
    if (!table)
        return editor;
    var below = table.findBelow(table.cell.key);
    var rowIndex = table.rowIndex, row = table.row;
    var index = typeof at === 'undefined' ? table.rowIndex : at;
    editor.withoutNormalizing(function () {
        if (table.height === 1 || !below) {
            table.getRows(index).forEach(function (cell) {
                if (cell.rowspan === 1)
                    return;
                editor.setNodeByKey(cell.key, {
                    type: cell.block.type,
                    data: __assign(__assign({}, cell.block.data.toObject()), { rowspan: cell.rowspan - 1 }),
                });
            });
            editor.removeNodeByKey(row.key);
            if (table.height === 1) {
                editor.removeNodeByKey(table.currentTable.key);
            }
        }
        else {
            var newNodes_1 = below.rowBlock.nodes.toArray().map(function (b) {
                if (!Block.isBlock(b))
                    return null;
                return b.toJSON();
            });
            var inserted_1 = {};
            table.getRows(index).forEach(function (cell) {
                if (cell.rowspan === 1)
                    return;
                if (cell.isTopOfMergedCell) {
                    var newBlock_1 = Block.create({
                        type: cell.block.type,
                        data: __assign(__assign({}, cell.block.data.toObject()), { rowspan: cell.rowspan - 1 }),
                        nodes: cell.block.nodes,
                    });
                    if (inserted_1[cell.key])
                        return;
                    newNodes_1.splice(cell.nodeIndex, 0, newBlock_1.toJSON());
                    inserted_1[cell.key] = true;
                }
                else {
                    editor.setNodeByKey(cell.key, {
                        type: cell.block.type,
                        data: __assign(__assign({}, cell.block.data.toObject()), { rowspan: cell.rowspan - 1 }),
                    });
                }
            });
            var newBlock = Block.fromJSON(__assign(__assign({}, below.rowBlock.toJSON()), { nodes: newNodes_1 }));
            editor.removeNodeByKey(row.key);
            editor.replaceNodeByKey(below.rowBlock.key, newBlock);
        }
        var newRow = TableLayout.findRowBlock(editor, rowIndex, opts);
        if (!newRow) {
            // TODO: find table when unfocused from table.
            return;
        }
        editor.moveTo(newRow.key);
    });
}

function removeColumn(opts, editor, at) {
    var table = TableLayout.create(editor, opts);
    if (!table)
        return editor;
    var columnIndex = typeof at === 'undefined' ? table.columnIndex : at;
    editor.withoutNormalizing(function () {
        table.filterHorizontalMergedCellsBy(columnIndex).forEach(function (cell) {
            editor.setNodeByKey(cell.key, {
                type: cell.block.type,
                data: __assign(__assign({}, cell.block.data.toObject()), { colspan: cell.colspan - 1 }),
            });
        });
        // Remove the cell from every row
        if (table.width > 1) {
            table.table.forEach(function (row, index) {
                var cell = row[columnIndex];
                if (cell.colspan === 1) {
                    editor.removeNodeByKey(cell.key);
                }
                if (cell.rowBlock.nodes.size === 1 && cell.colspan === 1) {
                    table.getRows(index).forEach(function (cell) {
                        if (cell.rowspan > 1) {
                            editor.setNodeByKey(cell.key, {
                                type: cell.block.type,
                                data: __assign(__assign({}, cell.block.data.toObject()), { rowspan: cell.rowspan - 1 }),
                            });
                        }
                    });
                    editor.removeNodeByKey(cell.rowBlock.key);
                }
            });
        }
        else {
            // If last column, clear text in cells instead
            editor.removeNodeByKey(table.currentTable.key);
        }
    });
}

function mergeBelow(opts, editor) {
    var currentCell = TableLayout.currentCell(editor, opts);
    if (!currentCell)
        return;
    var key = currentCell.key;
    var table = TableLayout.create(editor, opts);
    if (!table)
        return;
    if (table.isLastRow(key) || !currentCell)
        return;
    var current = table.findCellBy(key);
    var below = table.findBelow(key);
    if (!below || !current)
        return;
    mergeCells(editor, current.key, below.key, opts);
}

function mergeRight(opts, editor) {
    var currentCell = TableLayout.currentCell(editor, opts);
    if (!currentCell)
        return;
    var key = currentCell.key;
    var table = TableLayout.create(editor, opts);
    if (!table)
        return;
    if (table.isLastColumn(key))
        return;
    var current = table.findCellBy(key);
    var right = table.getRight(key);
    if (!right || !current)
        return;
    mergeCells(editor, current.key, right.key, opts);
}

function mergeSelection(opts, editor, store) {
    var anchorCellBlock = store.getAnchorCellBlock();
    var focusCellBlock = store.getFocusCellBlock();
    if (!anchorCellBlock || !focusCellBlock)
        return;
    if (anchorCellBlock.key === focusCellBlock.key)
        return;
    mergeCells(editor, anchorCellBlock.key, focusCellBlock.key, opts);
    return editor;
}

function createCell(opts, text, data) {
    if (opts === void 0) { opts = defaultOptions; }
    if (text === void 0) { text = ''; }
    var typeCell = opts.typeCell, typeContent = opts.typeContent;
    return Block.fromJSON({
        type: typeCell,
        object: 'block',
        nodes: [
            {
                object: 'block',
                type: typeContent,
                nodes: [Text.create(text).toJSON()],
            },
        ],
        data: __assign({ width: opts.defaultColumnWidth }, data),
    });
}

function createRow(opts, columns, columnWidth) {
    if (columnWidth === void 0) { columnWidth = opts.defaultColumnWidth; }
    var cellNodes = Range(0, columns)
        .map(function () { return createCell(opts, '', { width: "" + columnWidth }).toJSON(); })
        .toArray();
    return Block.fromJSON({
        object: 'block',
        type: opts.typeRow,
        nodes: cellNodes,
        data: {},
    });
}

function insertAbove(opts, editor) {
    var rowIndex = getRowIndex(editor, opts);
    if (rowIndex === null)
        return editor;
    var table = TableLayout.create(editor, opts);
    if (!table)
        return editor;
    if (rowIndex === 0) {
        var newRow = createRow(opts, table.table[table.rowIndex].length);
        return editor
            .insertNodeByKey(table.currentTable.key, rowIndex, newRow)
            .moveToEndOfNode(newRow.nodes.get(table.columnIndex));
    }
    else {
        var len_1 = 0;
        table.table[table.rowIndex].forEach(function (cell) {
            if (cell.rowspan > 1 && !cell.isTopOfMergedCell) {
                editor.setNodeByKey(cell.key, {
                    type: cell.block.type,
                    data: __assign(__assign({}, cell.block.data.toObject()), { rowspan: cell.rowspan + 1 }),
                });
            }
            else {
                len_1++;
            }
        });
        var newRow = createRow(opts, len_1);
        return editor
            .insertNodeByKey(table.currentTable.key, rowIndex, newRow)
            .moveToEndOfNode(newRow.nodes.get(table.columnIndex));
    }
}

function insertBelow(opts, editor) {
    var rowIndex = getRowIndex(editor, opts);
    if (rowIndex === null)
        return editor;
    var table = TableLayout.create(editor, defaultOptions);
    if (!table)
        return editor;
    if (rowIndex + 1 === table.table.length) {
        var newRow = createRow(opts, table.table[0].length);
        return editor
            .insertNodeByKey(table.currentTable.key, rowIndex + 1, newRow)
            .moveToEndOfNode(newRow.nodes.get(table.columnIndex));
    }
    else {
        table.table[rowIndex + 1]
            .filter(function (cell) { return cell.rowspan > 1 && !cell.isTopOfMergedCell; })
            .forEach(function (cell) {
            editor.setNodeByKey(cell.key, {
                type: cell.block.type,
                data: __assign(__assign({}, cell.block.data.toObject()), { rowspan: cell.rowspan + 1 }),
            });
        });
        var newRowLength = table.table[rowIndex + 1].filter(function (cell) {
            return cell.rowspan === 1 || cell.isTopOfMergedCell;
        }).length;
        var newRow = createRow(opts, newRowLength);
        return editor
            .insertNodeByKey(table.currentTable.key, rowIndex + 1, newRow)
            .moveToEndOfNode(newRow.nodes.get(table.columnIndex));
    }
}

function insertLeft(opts, editor, at) {
    var table = TableLayout.create(editor, opts);
    if (!table)
        return editor;
    var columnIndex = typeof at !== 'undefined' ? at : table.columnIndex;
    var added = {};
    table.table.forEach(function (row) {
        var cell = row[columnIndex];
        if (cell.colspan === 1 || table.table.length === 1) {
            if (added[cell.rowKey])
                return;
            var newCell = createCell(opts, '', { rowspan: "" + cell.rowspan });
            editor.insertNodeByKey(cell.rowKey, cell.nodeIndex, newCell);
            added[cell.rowKey] = true;
        }
        else {
            editor.setNodeByKey(cell.key, {
                type: cell.block.type,
                data: __assign(__assign({}, cell.block.data.toObject()), { colspan: cell.colspan + 1 }),
            });
        }
    });
}

function insertRight(opts, editor, at) {
    var table = TableLayout.create(editor, opts);
    if (!table)
        return editor;
    var columnIndex = typeof at !== 'undefined' ? at : table.columnIndex;
    var added = {};
    if (table.table[0].length === columnIndex) {
        table.table.forEach(function (row, i) {
            var cell = row[columnIndex];
            var rowKey = table.currentTable.nodes.get(i).key;
            var newCell = createCell(opts, '');
            editor.insertNodeByKey(rowKey, cell.nodeIndex + 1, newCell);
        });
    }
    else {
        table.table.forEach(function (row) {
            var cell = row[columnIndex];
            if (cell.colspan === 1 || table.table.length === 1) {
                if (added[cell.rowKey])
                    return;
                var newCell = createCell(opts, '', { rowspan: "" + cell.rowspan });
                editor.insertNodeByKey(cell.rowKey, cell.nodeIndex + 1, newCell);
                added[cell.rowKey] = true;
            }
            else {
                editor.setNodeByKey(cell.key, {
                    type: cell.block.type,
                    data: __assign(__assign({}, cell.block.data.toObject()), { colspan: cell.colspan + 1 }),
                });
            }
        });
    }
}

function createTable(opts, columns, rows, tableOptions) {
    var rowNodes = Range(0, rows)
        .map(function () { return createRow(opts, columns, tableOptions && tableOptions.columnWidth).toJSON(); })
        .toArray();
    return Block.fromJSON({
        object: 'block',
        type: opts.typeTable,
        nodes: rowNodes,
        data: {
            maxWidth: tableOptions && tableOptions.maxWidth,
        },
    });
}

function insertTable(opts, editor, columns, rows, tableOption) {
    if (columns === void 0) { columns = 2; }
    if (rows === void 0) { rows = 2; }
    var value = editor.value;
    if (!value.selection.start.key)
        return false;
    var table = createTable(opts, columns, rows, tableOption);
    var text = {
        object: 'block',
        type: 'paragraph',
        nodes: [Text.create('').toJSON()],
    };
    return editor
        .insertBlock(Block.fromJSON(text))
        .moveToStartOfPreviousBlock()
        .insertBlock(Block.fromJSON(text))
        .insertBlock(table);
}

function removeTable(opts, editor) {
    if (opts === void 0) { opts = defaultOptions; }
    var table = TableLayout.currentTable(editor, opts);
    if (!table)
        return editor;
    return editor.deselect().removeNodeByKey(table.key);
}

function splitCell(opts, editor) {
    var table = TableLayout.create(editor, opts);
    if (!table)
        return editor;
    var current = table.cell;
    var cell = table.findCellBy(current.key);
    if (!cell)
        return editor;
    editor.withoutNormalizing(function () {
        if (cell.rowspan === 1 && cell.colspan === 1)
            return editor;
        for (var y = 0; y < cell.rowspan; y++) {
            var targetIndex = 0;
            var prevCellKey = '';
            for (var _i = 0, _a = table.table[cell.row + y]; _i < _a.length; _i++) {
                var c = _a[_i];
                if (c.key === cell.key) {
                    break;
                }
                else {
                    if (prevCellKey !== c.key) {
                        targetIndex++;
                    }
                }
            }
            for (var x = 0; x < cell.colspan; x++) {
                if (y === 0) {
                    if (x === 0) {
                        editor.setNodeByKey(cell.key, {
                            type: cell.block.type,
                            data: __assign(__assign({}, cell.block.data.toObject()), { colspan: 1, rowspan: 1 }),
                        });
                    }
                    else {
                        var newCell = createCell(opts, '');
                        editor.insertNodeByKey(cell.rowKey, cell.col + 1, newCell);
                    }
                }
                else {
                    var newCell = createCell(opts, '');
                    var row = table.currentTable.nodes.get(y + cell.row);
                    editor.insertNodeByKey(row.key, targetIndex, newCell);
                }
            }
        }
    });
}

var ComponentStore = /** @class */ (function () {
    function ComponentStore() {
        var _this = this;
        // INFO: Use MAP with editor as key
        //       This is needed to support multi editor and singleton plugin.
        this.resizeDisableMap = new Map();
        this.resizeDisableEmitterMap = new Map();
        this.anchorCellBlock = null;
        this.focusCellBlock = null;
        this.isCellSelecting = false;
        this.subscribeDisableResizing = function (editor, f) {
            var emitters = _this.resizeDisableEmitterMap.get(editor) || [];
            _this.resizeDisableEmitterMap.set(editor, __spreadArrays(emitters, [f]));
            f(_this.resizeDisableMap.get(editor) || false);
        };
        this.setDisableResizing = function (editor, v) {
            _this.resizeDisableMap.set(editor, v);
            var emitters = _this.resizeDisableEmitterMap.get(editor) || [];
            emitters.forEach(function (e) {
                e(v);
            });
        };
        this.setAnchorCellBlock = function (b) { return (_this.anchorCellBlock = b); };
        this.getAnchorCellBlock = function () { return _this.anchorCellBlock; };
        this.setFocusCellBlock = function (b) { return (_this.focusCellBlock = b); };
        this.getFocusCellBlock = function () { return _this.focusCellBlock; };
        this.setCellSelecting = function (editor) {
            _this.isCellSelecting = true;
            // Disable resizing when cell selection started
            var emitters = _this.resizeDisableEmitterMap.get(editor) || [];
            emitters.forEach(function (e) {
                e(true);
            });
        };
        this.clearCellSelecting = function (editor) {
            _this.isCellSelecting = false;
            var v = _this.resizeDisableMap.get(editor);
            var emitters = _this.resizeDisableEmitterMap.get(editor) || [];
            emitters.forEach(function (e) {
                e(!!v);
            });
        };
        this.getCellSelecting = function () { return _this.isCellSelecting; };
        this.dispose = function () {
            _this.resizeDisableMap.clear();
            _this.resizeDisableEmitterMap.clear();
        };
    }
    return ComponentStore;
}());

var handlerSelector = '[data-resize-handle]';
var useResizableTable = function (props) {
    var ref = useRef(null);
    var createSize = function () {
        if (!ref.current)
            return;
        var table = ref.current;
        var cells = Array.from(table.querySelectorAll('th, td'));
        return cells.reduce(function (acc, cell) {
            if (!cell.dataset || !cell.dataset.key)
                return acc;
            acc[cell.dataset.key] = cell.offsetWidth;
            return acc;
        }, {});
    };
    useEffect(function () {
        if (!props.disableResizing)
            return;
        document.querySelectorAll(handlerSelector).forEach(function (el) {
            el.parentNode && el.parentNode.removeChild(el);
        });
    }, [props.disableResizing]);
    useEffect(function () {
        if (!ref.current)
            return;
        var size = createSize();
        if (size) {
            props.onInit && props.onInit(size);
        }
    }, []);
    var update = function () {
        if (!ref.current)
            return;
        var size = createSize();
        if (size) {
            props.onUpdate && props.onUpdate(size);
        }
    };
    useEffect(function () {
        if (!ref.current)
            return;
        var table = ref.current;
        var isResizing = false;
        var onTableMouseOver = function (e) {
            if (!e.target || !(e.target instanceof HTMLElement))
                return;
            if (isResizing)
                return;
            var tagname = e.target.tagName.toLowerCase();
            if (tagname !== 'td' && tagname !== 'th')
                return;
            if (!parent)
                return;
            var firstRow = getFirstRow(table);
            if (!firstRow)
                return;
            Array.from(firstRow.children).forEach(function (el) {
                el.style.position = 'relative';
                var range = getRangeXOf(el);
                if (!range)
                    return;
                getBoundaries(table).forEach(function (boundary) {
                    if (boundary > range.start && boundary <= range.end) {
                        var div = createDiv(table.offsetHeight, boundary - range.start);
                        el.appendChild(div);
                        var pageX_1 = 0;
                        var resizedValues_1 = {};
                        var rows_1 = [];
                        var onMouseDown = function (e) {
                            e.preventDefault();
                            if (!e.target || !(e instanceof MouseEvent))
                                return;
                            removeHandles(table, e.target);
                            if (!!props.disableResizing)
                                return;
                            isResizing = true;
                            rows_1 = createRowData(table);
                            pageX_1 = e.pageX;
                            document.addEventListener('mousemove', onMouseMove_1);
                            document.addEventListener('mouseup', onMouseUp_1);
                            props.onResizeStart && props.onResizeStart(e);
                        };
                        var onMouseMove_1 = function (e) {
                            if (!isResizing)
                                return;
                            var diffX = e.pageX - pageX_1;
                            resizedValues_1 = updateCellWidths(rows_1, boundary, diffX, props.minimumCellWidth);
                            props.onResize && props.onResize(e, resizedValues_1);
                        };
                        var onMouseUp_1 = function (e) {
                            isResizing = false;
                            var diffX = e.pageX - pageX_1;
                            resizedValues_1 = updateCellWidths(rows_1, boundary, diffX, props.minimumCellWidth);
                            props.onResizeStop && props.onResizeStop(e, resizedValues_1);
                            pageX_1 = 0;
                            removeHandles(table, e.relatedTarget);
                            document.removeEventListener('mousemove', onMouseMove_1);
                            document.removeEventListener('mouseup', onMouseUp_1);
                        };
                        var onMouseOut_1 = function (e) {
                            removeHandles(table, e.relatedTarget);
                            parent.removeEventListener('mouseout', onMouseOut_1);
                            document.removeEventListener('mouseleave', onMouseLeave_1);
                        };
                        var onMouseLeave_1 = function (e) {
                            isResizing = false;
                            removeHandles(table, e.relatedTarget);
                            parent.removeEventListener('mouseout', onMouseOut_1);
                            document.removeEventListener('mouseleave', onMouseLeave_1);
                        };
                        var onHandleMouseOver = function (e) {
                            props.onHandleHover && props.onHandleHover();
                        };
                        div.addEventListener('mousedown', onMouseDown);
                        div.addEventListener('mouseover', onHandleMouseOver);
                        parent.addEventListener('mouseout', onMouseOut_1);
                        document.addEventListener('mouseleave', onMouseLeave_1);
                    }
                });
            });
        };
        var onTableMouseOut = function (e) {
            if (!(e.relatedTarget instanceof HTMLElement))
                return;
            if (table.contains(e.relatedTarget))
                return;
            table.querySelectorAll(handlerSelector).forEach(function (el) {
                el.parentNode && el.parentNode.removeChild(el);
            });
        };
        table.addEventListener('mouseover', onTableMouseOver);
        table.addEventListener('mouseout', onTableMouseOut);
        if (!!props.disableResizing) {
            table.removeEventListener('mouseover', onTableMouseOver);
            table.removeEventListener('mouseout', onTableMouseOut);
        }
        return function () {
            table.removeEventListener('mouseover', onTableMouseOver);
            table.removeEventListener('mouseout', onTableMouseOut);
        };
    }, [props.disableResizing, ref.current]);
    return { ref: ref, update: update };
};
function updateCellWidths(rows, boundary, diffX, minimumWidth) {
    // Find target cells
    var targets = [];
    rows.forEach(function (row, rowIndex) {
        row.children.forEach(function (cell, colIndex) {
            if (!cell.ref.dataset || !cell.ref.dataset.key)
                return;
            if (cell.x < boundary && cell.x + cell.width >= boundary) {
                targets.push({ rowIndex: rowIndex, colIndex: colIndex, colspan: cell.colspan, width: cell.width });
            }
        });
    });
    var adjust = 0;
    // saturated or not?
    var saturated = targets.reduce(function (acc, target) {
        var p = rows[target.rowIndex].children[target.colIndex];
        if (!p)
            return acc;
        if (acc)
            return acc;
        if (p.ref.offsetWidth !== minimumWidth && p.width + diffX < minimumWidth) {
            adjust = minimumWidth - (p.width + diffX);
            return false;
        }
        adjust = 0;
        return p.width + diffX <= minimumWidth;
    }, false);
    var value = (rows || []).reduce(function (acc, row, rowIndex) {
        var hasCurrent = false;
        var hasNext = false;
        row.children.forEach(function (cell) {
            if (!cell.ref.dataset || !cell.ref.dataset.key)
                return acc;
            // If saturated disable resizing
            if (saturated)
                return;
            // If col merged and move inner slider, keep width.
            if (cell.colspan >= 2 && boundary < cell.x + cell.width && boundary > cell.x) {
                acc.value[cell.ref.dataset.key] = cell.width;
                cell.ref.style.width = cell.width + "px";
                return acc;
            }
            if (cell.x < boundary && cell.x + cell.width >= boundary) {
                if (!hasCurrent) {
                    hasCurrent = true;
                    cell.ref.style.width = Math.max(cell.width + diffX, minimumWidth) + "px";
                    acc.value[cell.ref.dataset.key] = cell.ref.offsetWidth;
                    return acc;
                }
            }
            if (hasCurrent && !hasNext) {
                hasNext = true;
                cell.ref.style.width = Math.max(cell.width - diffX - adjust, minimumWidth) + "px";
                acc.value[cell.ref.dataset.key] = cell.ref.offsetWidth;
                return acc;
            }
            acc.value[cell.ref.dataset.key] = cell.ref.offsetWidth;
            return acc;
        });
        return acc;
    }, { value: {} }).value;
    return value;
}
function createRowData(table) {
    var t = getRows(table);
    var acc = [];
    for (var y = 0; y < t.length; y++) {
        var row = t[y];
        for (var x = 0; x < row.children.length; x++) {
            var cell = row.children[x];
            if (!acc[y])
                acc[y] = [];
            // skip
            var cx = x;
            while (acc[y][cx]) {
                cx++;
            }
            acc[y][cx] = { cell: cell, width: cell.offsetWidth };
            for (var cy = 0; cy < cell.rowSpan - 1; cy++) {
                if (!acc[y + cy + 1])
                    acc[y + cy + 1] = [];
                acc[y + cy + 1][cx] = { cell: cell, width: cell.offsetWidth };
            }
        }
    }
    return t.map(function (row, y) {
        var children = acc[y].reduce(function (acc, cell) {
            var w = cell.width;
            var colspan = cell.cell.colSpan || 1;
            acc.children.push({ x: acc.x, width: w, colspan: colspan, ref: cell.cell });
            acc.x += w;
            return acc;
        }, { children: [], x: 0 }).children;
        return { ref: row, children: children };
    });
}
function removeHandles(table, target) {
    table.querySelectorAll(handlerSelector).forEach(function (el) {
        if (el !== target) {
            el.parentNode && el.parentNode.removeChild(el);
        }
    });
}
function getBoundaries(table) {
    var rows = Array.from(table.querySelectorAll('tr'));
    var layout = [];
    for (var y = 0; y < rows.length; y++) {
        var row = rows[y];
        if (!layout[y])
            layout[y] = [];
        for (var x = 0; x < row.children.length; x++) {
            var cell = row.children[x];
            if (!cell)
                continue;
            var offset = 0;
            while (true) {
                if (!layout[y][x + offset]) {
                    layout[y][x + offset] = cell.offsetWidth;
                    break;
                }
                offset++;
            }
            for (var r = 1; r < (cell.rowSpan || 1); r++) {
                if (!layout[y + r])
                    layout[y + r] = [];
                layout[y + r][x] = cell.offsetWidth;
            }
        }
    }
    var dict = {};
    layout.forEach(function (row) {
        var x = 0;
        row.forEach(function (width) {
            dict[width + x] = true;
            x += width;
        });
    });
    return Object.keys(dict).map(Number);
}
function getRows(table) {
    var cells = table.querySelectorAll('th, td');
    if (!cells)
        return [];
    var rows = Array.from(cells).reduce(function (acc, cell) {
        if (!cell.parentElement)
            return acc;
        if (acc.m.has(cell.parentElement))
            return acc;
        acc.m.set(cell.parentElement, true);
        acc.rows.push(cell.parentElement);
        return acc;
    }, { m: new Map(), rows: [] }).rows;
    return rows;
}
function getFirstRow(table) {
    var cell = table.querySelector('th, td');
    if (!cell || !cell.parentElement)
        return null;
    return cell.parentElement;
}
function getRangeXOf(cell) {
    var row = cell.parentElement;
    if (!row)
        return null;
    var range = Array.from(row.children).reduce(function (acc, e, i) {
        if (!(e instanceof HTMLElement))
            return acc;
        if (acc.reached)
            return acc;
        acc.range.start = i === 0 ? 0 : acc.range.end;
        acc.range.end = acc.range.start + e.offsetWidth;
        acc.reached = acc.reached || e === cell;
        return acc;
    }, { range: { start: 0, end: 0 }, reached: false }).range;
    return range;
}
function createDiv(height, offset) {
    var div = document.createElement('div');
    div.style.top = '0';
    div.style.left = offset - 10 + 'px';
    div.style.width = '10px';
    div.style.position = 'absolute';
    div.style.cursor = 'col-resize';
    div.style.userSelect = 'none';
    div.style.height = height + 'px';
    div.dataset.resizeHandle = 'true';
    div.style.zIndex = '1';
    div.contentEditable = 'false';
    return div;
}

var tableStyle = {
    borderSpacing: 0,
    Layout: 'fixed',
    wordBreak: 'break-word',
};
var InnerTable = forwardRef(function (props, tableRef) {
    var _a = useState(false), disableResizing = _a[0], forceUpdate = _a[1];
    var maxWidth = typeof props.maxWidth === 'undefined' ? 'auto' : props.maxWidth + 'px';
    var onInit = useCallback(function (values) {
        props.onInit(props.editor, values);
    }, [props.editor]);
    var onUpdate = useCallback(function (values) {
        props.onUpdate(props.editor, values);
    }, [props.editor]);
    var onResizeStop = useCallback(function (e, values) {
        props.editor.blur();
        props.onResizeStop(props.editor, values);
    }, [props.editor]);
    var onResizeStart = useCallback(function (e) {
        e.stopPropagation();
        props.editor.blur();
        removeSelection(props.editor);
        props.store.setAnchorCellBlock(null);
        props.store.setFocusCellBlock(null);
    }, [props.editor]);
    var _b = useResizableTable({
        disableResizing: disableResizing,
        maxWidth: props.maxWidth,
        minimumCellWidth: props.minimumCellWidth,
        onResizeStart: onResizeStart,
        // onResize,
        onResizeStop: onResizeStop,
        onInit: onInit,
        onUpdate: onUpdate,
        onHandleHover: props.onHandleMouseOver,
    }), ref = _b.ref, update = _b.update;
    useEffect(function () {
        props.store.subscribeDisableResizing(props.editor, function (v) {
            forceUpdate(v);
        });
    }, []);
    useEffect(function () {
        if (!ref.current) {
            ref.current = props.attributes && props.attributes.ref && props.attributes.ref.current;
        }
        update();
    }, []);
    useImperativeHandle(tableRef, function () { return ({
        update: function () {
            update();
        },
    }); });
    var onDragStart = useCallback(function (e) {
        e.preventDefault();
    }, []);
    return (createElement("table", __assign({ ref: ref, style: __assign(__assign(__assign({}, props.style), tableStyle), { maxWidth: maxWidth }) }, props.attributes, { onDragStart: onDragStart }), props.children));
});
var Table = memo(InnerTable);
function updateWidth(editor, value) {
    Object.keys(value).forEach(function (k) {
        var n = editor.value.document.getNode(k);
        if (!Block.isBlock(n))
            return;
        editor.setNodeByKey(k, {
            type: n.type,
            data: __assign(__assign({}, n.data.toObject()), { width: value[k] }),
        });
    });
}
var contentStyle = {
    margin: 0,
};
var Content = memo(function (props) {
    return (createElement("p", __assign({ style: contentStyle }, props.attributes), props.children));
});
var Cell = memo(function (props) {
    var width = typeof props.node.data.get('width') === 'undefined' ? 'auto' : props.node.data.get('width') + 'px';
    var style = props.node.data.get('style') || {};
    var onMouseUp = useCallback(function (e) {
        props.store.clearCellSelecting(props.editor);
        window.removeEventListener('mouseup', onMouseUp);
    }, []);
    var onWindowClick = useCallback(function (e) {
        if (!findCurrentTable(props.editor, props.opts)) {
            removeSelection(props.editor);
            props.store.setAnchorCellBlock(null);
            props.store.setFocusCellBlock(null);
            window.removeEventListener('click', onWindowClick);
        }
    }, [props.editor, props.opts]);
    useEffect(function () {
        return function () {
            window.removeEventListener('mouseup', onMouseUp);
            window.removeEventListener('click', onWindowClick);
        };
    }, [onMouseUp, onWindowClick]);
    var tdStyles = __assign(__assign(__assign({}, props.opts.cellStyle), { width: width, minWidth: props.opts.minimumCellWidth + "px", verticalAlign: 'baseline' }), style);
    if (props.node.data.get('selectionColor')) {
        tdStyles.backgroundColor = props.node.data.get('selectionColor');
    }
    return (createElement("td", __assign({}, props.attributes, { onMouseDown: function (e) {
            if (!(e.target instanceof HTMLElement))
                return;
            props.store.setAnchorCellBlock(null);
            props.store.setFocusCellBlock(null);
            removeSelection(props.editor);
            props.store.setCellSelecting(props.editor);
            var anchorCellBlock = findCellBlockByElement(props.editor, e.target, props.opts);
            props.store.setAnchorCellBlock(anchorCellBlock);
            window.addEventListener('mouseup', onMouseUp);
            window.addEventListener('click', onWindowClick);
        }, onMouseOver: function (e) {
            e.stopPropagation();
            var anchorCellBlock = props.store.getAnchorCellBlock();
            if (anchorCellBlock === null)
                return;
            if (!(e.target instanceof HTMLElement))
                return;
            if (!props.store.getCellSelecting())
                return;
            var focusCellBlock = findCellBlockByElement(props.editor, e.target, props.opts);
            if (!focusCellBlock)
                return;
            var prevFocusBlock = props.store.getFocusCellBlock();
            if (focusCellBlock.key === (prevFocusBlock && prevFocusBlock.key))
                return;
            var t = TableLayout.create(props.editor, props.opts);
            if (!t) {
                removeSelection(props.editor);
                props.store.setAnchorCellBlock(null);
                props.store.setFocusCellBlock(null);
                return;
            }
            props.store.setFocusCellBlock(focusCellBlock);
            // HACK: Add ::selection style when greater than 1 cells selected.
            addSelectionStyle();
            var blocks = createSelectedBlockMap(props.editor, anchorCellBlock.key, focusCellBlock.key, props.opts);
            props.editor.withoutSaving(function () {
                t.table.forEach(function (row) {
                    row.forEach(function (cell) {
                        if (blocks[cell.key]) {
                            props.editor.setNodeByKey(cell.key, {
                                type: cell.block.type,
                                data: __assign(__assign({}, cell.block.data.toObject()), { selectionColor: props.opts.selectionColor }),
                            });
                        }
                        else {
                            props.editor.setNodeByKey(cell.key, {
                                type: cell.block.type,
                                data: __assign(__assign({}, cell.block.data.toObject()), { selectionColor: null }),
                            });
                        }
                    });
                });
            });
        }, colSpan: props.node.data.get('colspan'), rowSpan: props.node.data.get('rowspan'), style: tdStyles }), props.children));
});
var preventDefault = function (e) { return e.preventDefault(); };
function createRenderers(opts, ref, store) {
    return function (props, editor, next) {
        switch (props.node.type) {
            case opts.typeContent:
                return createElement(Content, { attributes: props.attributes },
                    " ",
                    props.children);
            case opts.typeTable:
                var maxWidth = props.node.data.get('maxWidth');
                return (createElement(Table, { ref: ref, editor: editor, store: store, onInit: updateWidth, onUpdate: updateWidth, onResizeStop: updateWidth, maxWidth: maxWidth, minimumCellWidth: opts.minimumCellWidth, style: opts.tableStyle, attributes: props.attributes },
                    createElement("tbody", __assign({}, props.attributes), props.children)));
            case opts.typeRow:
                return (createElement("tr", __assign({}, props.attributes, { style: opts.rowStyle, onDrag: preventDefault }), props.children));
            case opts.typeCell:
                return (createElement(Cell, { editor: editor, store: store, node: props.node, attributes: props.attributes, opts: opts }, props.children));
            default:
                return next();
        }
    };
}

function createSchema(opts) {
    var _a;
    var schema = {
        blocks: (_a = {},
            _a[opts.typeCell] = {
                parent: {
                    type: opts.typeRow,
                },
                nodes: [
                    {
                        match: { object: 'block' },
                        min: 1,
                    },
                ],
            },
            _a[opts.typeRow] = {
                parent: { type: opts.typeTable },
                nodes: [
                    {
                        match: { object: 'block', type: opts.typeCell },
                        min: 1,
                    },
                ],
            },
            _a[opts.typeTable] = {
                nodes: [
                    {
                        match: { object: 'block', type: opts.typeRow },
                        min: 1,
                    },
                ],
            },
            _a),
    };
    return { schema: schema };
}

function EditTable(options) {
    if (options === void 0) { options = defaultOptions; }
    var opts = __assign(__assign({}, defaultOptions), options);
    var ref = createRef();
    var store = new ComponentStore();
    function isSelectionInTable(editor) {
        var _a = editor.value, startBlock = _a.startBlock, endBlock = _a.endBlock;
        if (!startBlock || !endBlock)
            return false;
        return isInCell(editor, startBlock, opts) || isInCell(editor, endBlock, opts);
    }
    function canSelectedCellsMerge(editor) {
        var t = TableLayout.create(editor, opts);
        if (!t)
            return false;
        var anchored = findAnchorCell(editor, opts);
        var focused = findFocusCell(editor, opts);
        if (!anchored || !focused)
            return false;
        return canMerge(editor, anchored.key, focused.key, opts);
    }
    function bindEditorWithoutSelectionCheck(fn) {
        return function (editor) {
            var args = [];
            for (var _i = 1; _i < arguments.length; _i++) {
                args[_i - 1] = arguments[_i];
            }
            return fn.apply(void 0, [opts, editor].concat(args));
        };
    }
    function bindEditorWithStore(fn) {
        return function (editor) {
            var args = [];
            for (var _i = 1; _i < arguments.length; _i++) {
                args[_i - 1] = arguments[_i];
            }
            if (!isSelectionInTable(editor)) {
                return editor;
            }
            // update table size
            ref.current && ref.current.update();
            return fn.apply(void 0, [opts, editor, store].concat(args));
        };
    }
    function bindEditor(fn) {
        return function (editor) {
            var args = [];
            for (var _i = 1; _i < arguments.length; _i++) {
                args[_i - 1] = arguments[_i];
            }
            if (!isSelectionInTable(editor)) {
                return editor;
            }
            // update table size
            ref.current && ref.current.update();
            return fn.apply(void 0, [opts, editor].concat(args));
        };
    }
    function onBackspace(event, editor, next) {
        var value = editor.value;
        var selection = value.selection;
        var key = editor.value.focusBlock && editor.value.focusBlock.key;
        var cell = TableLayout.currentCell(editor, opts);
        if (!cell)
            return next();
        // INFO: Skip backspace when first node and offset 0
        if (editor.value.focusBlock.type === opts.typeCell) {
            if (selection.start.offset != 0)
                return next();
        }
        else {
            if (selection.start.offset != 0 || key !== cell.nodes.get(0).key)
                return next();
        }
        event.preventDefault();
    }
    function onDelete(event, editor, next) {
        var value = editor.value;
        var selection = value.selection;
        if (selection.end.offset != value.startText.text.length)
            return next();
        event.preventDefault();
    }
    function onKeyDown(event, editor, next) {
        var value = editor.value;
        var document = value.document, selection = value.selection;
        var start = selection.start, isCollapsed = selection.isCollapsed;
        if (!start || !start.key)
            return next();
        var startNode = document.getDescendant(start.key);
        if (!startNode)
            return next();
        var previous = document.getPreviousText(startNode.key);
        var prevBlock = previous ? document.getClosestBlock(previous.key) : null;
        if (event.key === 'Delete' || (event.ctrlKey && event.key === 'd')) {
            var nextBock = document.getNextBlock(startNode.key);
            if (
            // HACK: For ctrl + d key short cut
            selection.end.offset >= value.startText.text.length - 1 &&
                editor.value.startBlock.type !== opts.typeContent &&
                nextBock &&
                nextBock.type === opts.typeContent) {
                event.preventDefault();
                if (selection.end.offset === value.startText.text.length - 1) {
                    editor.deleteForward(1);
                }
                return;
            }
        }
        if (event.key === 'Backspace' || (event.ctrlKey && event.key === 'h')) {
            if (
            // HACK: For ctrl + h key short cut
            selection.start.offset <= 1 &&
                editor.value.startBlock.type !== opts.typeContent &&
                prevBlock &&
                prevBlock.type === opts.typeContent) {
                event.preventDefault();
                if (selection.start.offset === 1) {
                    editor.deleteBackward(1);
                }
                return;
            }
        }
        // When next block is table check keydown with table logic.
        if (!isSelectionInTable(editor)) {
            return next();
        }
        // INFO: It is needed to prevent replace some <td /> s when some cells selected.
        if (value.startBlock !== value.endBlock) {
            event.preventDefault();
            return;
        }
        if (isCollapsed && start.isAtStartOfNode(startNode)) {
            if (!prevBlock)
                return next();
            if (prevBlock.type === opts.typeCell) {
                if (['Backspace', 'Delete', 'Enter'].includes(event.key) || (event.ctrlKey && event.key === 'h')) {
                    event.preventDefault();
                }
                else {
                    return next();
                }
            }
        }
        if (event.ctrlKey && event.key === 'd') {
            event.preventDefault();
            // INFO: Delete forward when it is not end of node
            if (selection.end.offset != value.startText.text.length) {
                editor.deleteForward(1);
                return next();
            }
            return next();
        }
        if (event.ctrlKey && event.key === 'h') {
            var key = editor.value.focusBlock && editor.value.focusBlock.key;
            var cell = TableLayout.currentCell(editor, opts);
            if (!cell)
                return next();
            // INFO: Skip backspace when first node and offset 0
            event.preventDefault();
            if (editor.value.focusBlock.type === opts.typeCell) {
                if (selection.start.offset != 0) {
                    editor.deleteBackward(1);
                    return next();
                }
            }
            else {
                if (selection.start.offset != 0 || key !== cell.nodes.get(0).key) {
                    editor.deleteBackward(1);
                    return next();
                }
            }
            return next();
        }
        switch (event.key) {
            case 'Backspace':
                return onBackspace(event, editor, next);
            case 'Delete':
                return onDelete(event, editor, next);
            default:
                return next();
        }
    }
    var renderer = createRenderers(opts, ref, store);
    var schema = createSchema(opts).schema;
    return {
        schema: schema,
        onKeyDown: onKeyDown,
        // For old version
        renderNode: renderer,
        // For slate-react@0.22.0~
        renderBlock: renderer,
        queries: {
            hasTablePlugin: function () { return true; },
            isSelectionInTable: isSelectionInTable,
            canSelectedCellsMerge: canSelectedCellsMerge,
            findCurrentTable: function (editor) { return findCurrentTable(editor, opts); },
        },
        commands: {
            disableResizing: bindEditorWithoutSelectionCheck(function (_, editor) {
                store.setDisableResizing(editor, true);
            }),
            enableResizing: bindEditorWithoutSelectionCheck(function (_, editor) {
                store.setDisableResizing(editor, false);
            }),
            insertTable: bindEditorWithoutSelectionCheck(insertTable),
            // row
            insertAbove: bindEditor(insertAbove),
            insertBelow: bindEditor(insertBelow),
            // column
            insertLeft: bindEditor(insertLeft),
            insertRight: bindEditor(insertRight),
            mergeRight: bindEditor(mergeRight),
            mergeBelow: bindEditor(mergeBelow),
            mergeSelection: bindEditorWithStore(mergeSelection),
            splitCell: bindEditor(splitCell),
            removeRow: bindEditor(removeRow),
            removeColumn: bindEditor(removeColumn),
            removeTable: bindEditor(removeTable),
        },
    };
}
function hasTablePlugin(e) {
    return e && e.hasTablePlugin && e.hasTablePlugin();
}

export { EditTable, hasTablePlugin };
